import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarRange, Plus, Trash2, Copy, Pencil, Building2, FileDown,
  Send, Archive, CalendarDays, Users, X, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "MONDAY", label: "Lunes" },
  { key: "TUESDAY", label: "Martes" },
  { key: "WEDNESDAY", label: "Miércoles" },
  { key: "THURSDAY", label: "Jueves" },
  { key: "FRIDAY", label: "Viernes" },
  { key: "SATURDAY", label: "Sábado" },
  { key: "SUNDAY", label: "Domingo" },
];
const STATUS = {
  draft: { label: "Borrador", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  published: { label: "Publicado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  archived: { label: "Archivado", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STEP_OPTS = [5, 10, 15, 30, 60];

// Subdivisiones estándar del campo. Los conflictos conocen su solapamiento
// físico (ver backend services/schedule_conflicts.py).
const STANDARD_ZONES = [
  "Campo completo",
  "Medio campo 1", "Medio campo 2",
  "Cuarto 1", "Cuarto 2", "Cuarto 3", "Cuarto 4",
  "Fondo 1", "Fondo 2",
];

const emptyEntry = () => ({
  team_id: "", day_of_week: "MONDAY", start_time: "16:30", end_time: "17:30",
  facility_id: "", field_area_id: "", notes: "",
});

export default function TrainingSchedules() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [schedules, setSchedules] = useState([]);
  const [selId, setSelId] = useState("");
  const [weekly, setWeekly] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null); // null = general
  const [periodDlg, setPeriodDlg] = useState(null);
  const [conflicts, setConflicts] = useState({ conflicts: [], counts: {} });
  const [facilities, setFacilities] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("weekly");

  // filters
  const [fTeam, setFTeam] = useState("all");
  const [fCategory, setFCategory] = useState("all");
  const [fFacility, setFFacility] = useState("all");
  const [fDay, setFDay] = useState("all");

  // dialogs
  const [schedDlg, setSchedDlg] = useState(null); // {mode:'create'|'edit'|'duplicate', data}
  const [entryDlg, setEntryDlg] = useState(null); // {mode, data}
  const [facDlg, setFacDlg] = useState(false);
  const [publishDlg, setPublishDlg] = useState(null); // preview summary
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(15);

  const selected = schedules.find((s) => s.id === selId);

  const loadSchedules = useCallback(async () => {
    const r = await api.get("/schedules");
    setSchedules(r.data);
    return r.data;
  }, []);

  const loadWeekly = useCallback(async (id, periodId) => {
    if (!id) { setWeekly(null); setPeriods([]); setConflicts({ conflicts: [], counts: {} }); return; }
    const q = periodId ? `?period_id=${periodId}` : "";
    const [w, c] = await Promise.all([
      api.get(`/schedules/${id}/weekly${q}`),
      api.get(`/schedules/${id}/conflicts`).catch(() => ({ data: { conflicts: [], counts: {} } })),
    ]);
    setWeekly(w.data);
    setPeriods(w.data.periods || []);
    setConflicts(c.data);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [sch, fac, tm] = await Promise.all([
          api.get("/schedules"), api.get("/schedules/facilities"), api.get("/teams"),
        ]);
        setSchedules(sch.data);
        setFacilities(fac.data);
        setTeams(tm.data);
        if (sch.data.length) setSelId(sch.data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { setActivePeriod(null); }, [selId]);
  useEffect(() => { loadWeekly(selId, activePeriod); }, [selId, activePeriod, loadWeekly]);

  const categories = useMemo(
    () => Array.from(new Set(teams.map((t) => t.category).filter(Boolean))).sort(),
    [teams],
  );
  const zonesFor = (fid) => (facilities.find((f) => f.id === fid)?.zones) || [];
  const normZone = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
  // Opciones de zona: las zonas ya definidas + zonas estándar que falten
  // (estas últimas con valor sentinela 'std:<nombre>'; el backend las crea al vuelo).
  const zoneOptions = (fid) => {
    if (!fid) return [];
    const zs = zonesFor(fid);
    const present = new Set(zs.map((z) => normZone(z.name)));
    const extra = STANDARD_ZONES
      .filter((n) => !present.has(normZone(n)))
      .map((n) => ({ id: `std:${n}`, name: n, std: true }));
    return [...zs.map((z) => ({ id: z.id, name: z.name })), ...extra];
  };

  const entries = weekly?.entries || [];
  const filtered = useMemo(() => entries.filter((e) => {
    if (fTeam !== "all" && e.team_id !== fTeam) return false;
    if (fCategory !== "all" && e.team_category !== fCategory) return false;
    if (fFacility !== "all" && e.facility_id !== fFacility) return false;
    if (fDay !== "all" && e.day_of_week !== fDay) return false;
    return true;
  }), [entries, fTeam, fCategory, fFacility, fDay]);

  // time slots present (sorted), used as grid rows
  const slots = useMemo(() => {
    const set = new Map();
    filtered.forEach((e) => set.set(`${e.start_time}|${e.end_time}`, [e.start_time, e.end_time]));
    return Array.from(set.values()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const activeDays = useMemo(() => {
    const used = new Set(filtered.map((e) => e.day_of_week));
    return DAYS.filter((d) => ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"].includes(d.key) || used.has(d.key));
  }, [filtered]);

  const cellEntries = (day, st, et) => filtered.filter(
    (e) => e.day_of_week === day && e.start_time === st && e.end_time === et);

  // ---------------- schedule CRUD ----------------
  const saveSchedule = async () => {
    const d = schedDlg.data;
    if (!d.season_label?.trim() || !d.name?.trim() || !d.start_date || !d.end_date) {
      toast.error("Completa temporada, nombre y fechas"); return;
    }
    setBusy(true);
    try {
      if (schedDlg.mode === "create") {
        const r = await api.post("/schedules", d);
        await loadSchedules(); setSelId(r.data.id);
      } else if (schedDlg.mode === "duplicate") {
        const r = await api.post("/schedules/duplicate", { source_schedule_id: schedDlg.sourceId, ...d });
        await loadSchedules(); setSelId(r.data.id);
        toast.success(`Horario duplicado (${r.data.copied_entries} franjas)`);
      } else {
        await api.put(`/schedules/${d.id}`, d);
        await loadSchedules();
      }
      setSchedDlg(null);
      if (schedDlg.mode !== "edit") toast.success("Horario creado como borrador");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const archiveSchedule = async () => {
    if (!window.confirm("Archivar el horario retirará del calendario los entrenamientos futuros no modificados manualmente. ¿Continuar?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/schedules/${selId}/archive`);
      toast.success(`Archivado · ${r.data.calendar_removed} entrenamientos futuros retirados`);
      await loadSchedules();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deleteSchedule = async () => {
    if (!window.confirm("Eliminar este horario y sus franjas. Los entrenamientos futuros no modificados manualmente se retirarán del calendario. ¿Seguro?")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/schedules/${selId}`);
      toast.success(`Eliminado · ${r.data.calendar_removed} entrenamientos retirados`);
      const list = await loadSchedules();
      setSelId(list[0]?.id || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // ---------------- entry CRUD ----------------
  const saveEntry = async () => {
    const d = entryDlg.data;
    if (!d.team_id) { toast.error("Elige un equipo"); return; }
    if (!d.start_time || !d.end_time || d.start_time >= d.end_time) {
      toast.error("La hora de fin debe ser posterior a la de inicio"); return;
    }
    setBusy(true);
    try {
      if (entryDlg.mode === "create") {
        await api.post(`/schedules/${selId}/entries`, { ...d, period_id: activePeriod || null });
      } else {
        await api.put(`/schedules/entries/${d.id}`, { ...d, period_id: activePeriod || null });
      }
      await loadWeekly(selId, activePeriod);
      const fr = await api.get("/schedules/facilities"); setFacilities(fr.data);
      setEntryDlg(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Eliminar esta asignación del horario?")) return;
    try { await api.delete(`/schedules/entries/${id}`); await loadWeekly(selId, activePeriod); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyToDay = async (entry, day) => {
    try { await api.post(`/schedules/entries/${entry.id}/copy-to-day`, { day_of_week: day }); await loadWeekly(selId, activePeriod); toast.success("Franja copiada"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // drag & drop: move entry to another day/slot
  const onDropCell = async (e, day, st, et) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/entry");
    if (!id) return;
    const entry = entries.find((x) => x.id === id);
    if (!entry) return;
    if (entry.day_of_week === day && entry.start_time === st && entry.end_time === et) return;
    try {
      await api.put(`/schedules/entries/${id}`, {
        team_id: entry.team_id, day_of_week: day, start_time: st, end_time: et,
        facility_id: entry.facility_id, field_area_id: entry.field_area_id,
        valid_from: entry.valid_from || "", valid_until: entry.valid_until || "",
        notes: entry.notes || "", period_id: activePeriod || null,
      });
      await loadWeekly(selId, activePeriod);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---------------- publish ----------------
  const openPublish = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/schedules/${selId}/sync-preview`);
      setPublishDlg(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const confirmPublish = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/schedules/${selId}/publish${force ? "?force=true" : ""}`);
      toast.success(`Publicado · ${r.data.created} nuevos, ${r.data.updated} actualizados, ${r.data.cancelled} cancelados, ${r.data.protected} protegidos`);
      setPublishDlg(null);
      await loadSchedules();
      await loadWeekly(selId, activePeriod);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Conflictos graves: revísalos o pulsa “Publicar de todas formas”.");
        setConflicts({ conflicts: e.response.data?.detail?.conflicts || [], counts: e.response.data?.detail?.counts || {} });
      } else { toast.error(formatApiError(e)); }
    } finally { setBusy(false); }
  };

  // ---------------- special periods ----------------
  const period = periods.find((p) => p.id === activePeriod);
  const savePeriod = async () => {
    const d = periodDlg.data;
    if (!d.name?.trim() || !d.start_date || !d.end_date) { toast.error("Completa nombre y fechas"); return; }
    setBusy(true);
    try {
      if (periodDlg.mode === "create") {
        const r = await api.post(`/schedules/${selId}/periods`, d);
        await loadWeekly(selId, activePeriod);
        setActivePeriod(r.data.id);
        toast.success("Periodo creado como borrador — añade sus franjas y publícalo");
      } else {
        await api.put(`/schedules/periods/${d.id}`, d);
        await loadWeekly(selId, activePeriod);
      }
      setPeriodDlg(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const deletePeriod = async (pid) => {
    if (!window.confirm("Eliminar este periodo especial. El horario general volverá automáticamente para esas fechas. ¿Continuar?")) return;
    try {
      const r = await api.delete(`/schedules/periods/${pid}`);
      toast.success(`Periodo eliminado${r.data.resync?.created != null ? ` · general restaurado (${r.data.resync.created})` : ""}`);
      setActivePeriod(null);
      await loadWeekly(selId, null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePeriodPublish = async (p, force = false) => {
    try {
      if (p.status === "published") {
        await api.post(`/schedules/periods/${p.id}/unpublish`);
        toast.success("Periodo despublicado — vuelve el horario general");
      } else {
        const r = await api.post(`/schedules/periods/${p.id}/publish${force ? "?force=true" : ""}`);
        toast.success(`Periodo publicado · ${r.data.created} nuevos, ${r.data.cancelled} sustituidos`);
      }
      await loadWeekly(selId, activePeriod);
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("El periodo tiene conflictos graves. ¿Publicar de todas formas?")) {
          await togglePeriodPublish(p, true);
        }
      } else { toast.error(formatApiError(e)); }
    }
  };

  const downloadPdf = () => {
    const q = fTeam !== "all" ? `?team_id=${fTeam}` : "";
    window.open(`${API}/export/schedule/${selId}/pdf${q}`, "_blank");
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

  if (loading) return <div className="p-8 text-slate-500" data-testid="sched-loading">Cargando horarios…</div>;

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-5" data-testid="schedules-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-purple-600 text-white grid place-items-center">
            <CalendarRange className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Horarios de entrenamiento</h1>
            <p className="text-sm text-slate-500 font-medium">Plantillas por temporada sincronizadas con el calendario · EF Concepción</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setFacDlg(true)} data-testid="sched-facilities-btn">
              <Building2 className="h-4 w-4 mr-1.5" /> Instalaciones
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => setSchedDlg({ mode: "create", data: { season_label: "2026-2027", name: "", start_date: "", end_date: "" } })}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="sched-new-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nuevo horario
            </Button>
          )}
        </div>
      </div>

      {schedules.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500" data-testid="sched-empty">
          Aún no hay horarios. {isAdmin ? "Crea el primero con “Nuevo horario”." : "Todavía no se ha publicado ningún horario."}
        </CardContent></Card>
      ) : (
        <>
          {/* Schedule selector + actions */}
          <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger className="w-[260px] font-bold" data-testid="sched-selector">
                <SelectValue placeholder="Selecciona un horario" />
              </SelectTrigger>
              <SelectContent>
                {schedules.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`sched-opt-${s.id}`}>
                    {s.name} · {s.season_label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${STATUS[selected.status]?.cls}`} data-testid="sched-status">
                {STATUS[selected.status]?.label}
              </span>
            )}
            {selected && (
              <span className="text-xs text-slate-500 font-medium">
                {selected.start_date} → {selected.end_date} · {selected.entries_count} franjas
              </span>
            )}
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="sched-pdf-btn">
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm"
                  onClick={() => setSchedDlg({ mode: "duplicate", sourceId: selId, data: { season_label: "", name: `${selected?.name || "Horario"} (copia)`, start_date: "", end_date: "" } })}
                  data-testid="sched-duplicate-btn">
                  <Copy className="h-4 w-4 mr-1.5" /> Duplicar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSchedDlg({ mode: "edit", data: { ...selected } })} data-testid="sched-edit-btn">
                  <Pencil className="h-4 w-4 mr-1.5" /> Editar
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={openPublish} disabled={busy} data-testid="sched-publish-btn">
                  <Send className="h-4 w-4 mr-1.5" /> Publicar y sincronizar
                </Button>
                {selected?.status !== "archived" && (
                  <Button variant="ghost" size="sm" className="text-amber-700" onClick={archiveSchedule} data-testid="sched-archive-btn">
                    <Archive className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-red-600" onClick={deleteSchedule} data-testid="sched-delete-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {/* View tabs + filters */}
          <div className="flex flex-wrap items-center gap-2">
            {[["weekly", "Semanal", CalendarDays], ["team", "Por equipo", Users], ["facility", "Por instalación", Building2]].map(([v, lbl, Icon]) => (
              <Button key={v} size="sm" variant={view === v ? "default" : "outline"}
                className={view === v ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
                onClick={() => setView(v)} data-testid={`sched-view-${v}`}>
                <Icon className="h-4 w-4 mr-1.5" /> {lbl}
              </Button>
            ))}
            <div className="flex-1" />
            <FilterSelect value={fTeam} onChange={setFTeam} testid="filter-team"
              options={[["all", "Todos los equipos"], ...teams.map((t) => [t.id, t.name])]} />
            <FilterSelect value={fCategory} onChange={setFCategory} testid="filter-category"
              options={[["all", "Todas las categorías"], ...categories.map((c) => [c, c])]} />
            <FilterSelect value={fFacility} onChange={setFFacility} testid="filter-facility"
              options={[["all", "Todas las instalaciones"], ...facilities.map((f) => [f.id, f.name])]} />
            <FilterSelect value={fDay} onChange={setFDay} testid="filter-day"
              options={[["all", "Todos los días"], ...DAYS.map((d) => [d.key, d.label])]} />
          </div>

          {/* Special periods bar */}
          <div className="flex flex-wrap items-center gap-2 bg-purple-50/60 border border-purple-200 rounded-xl p-2.5" data-testid="periods-bar">
            <span className="text-xs font-bold uppercase tracking-wide text-purple-700 px-1">Periodos</span>
            <button
              className={`text-sm font-bold px-3 py-1 rounded-full border ${!activePeriod ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-200 hover:border-purple-300"}`}
              onClick={() => setActivePeriod(null)} data-testid="period-general">
              Horario general
            </button>
            {periods.map((p) => (
              <button key={p.id}
                className={`text-sm font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${activePeriod === p.id ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-200 hover:border-purple-300"}`}
                onClick={() => setActivePeriod(p.id)} data-testid={`period-chip-${p.id}`}>
                {p.name}
                <span className={`text-[10px] px-1 rounded ${p.status === "published" ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"}`}>
                  {p.status === "published" ? "●" : "○"}
                </span>
              </button>
            ))}
            {isAdmin && (
              <Button variant="outline" size="sm" className="h-7 border-purple-300 text-purple-700"
                onClick={() => setPeriodDlg({ mode: "create", data: { name: "", start_date: "", end_date: "", team_ids: [], reason: "" } })}
                data-testid="period-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Periodo especial
              </Button>
            )}
          </div>

          {/* Active period banner */}
          {period && (
            <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5" data-testid="period-banner">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-amber-900">{period.name}
                  <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS[period.status]?.cls}`}>{STATUS[period.status]?.label}</span>
                </div>
                <div className="text-xs text-amber-800">
                  {period.start_date} → {period.end_date} · Sustituye al general para {(period.team_ids || []).length} equipo(s)
                  {period.reason ? ` · ${period.reason}` : ""}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setPeriodDlg({ mode: "edit", data: { ...period } })} data-testid="period-edit-btn">
                    <Pencil className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button size="sm" className={period.status === "published" ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    onClick={() => togglePeriodPublish(period)} data-testid="period-toggle-publish">
                    <Send className="h-4 w-4 mr-1" /> {period.status === "published" ? "Despublicar" : "Publicar periodo"}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePeriod(period.id)} data-testid="period-delete-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Conflicts summary */}
          {(conflicts.conflicts || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="conflicts-card">
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
                <AlertTriangle className="h-4 w-4 text-slate-500" />
                <span className="font-bold text-sm text-slate-700">Conflictos detectados</span>
                {conflicts.counts?.error > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200" data-testid="conflicts-error-count">{conflicts.counts.error} graves</span>}
                {conflicts.counts?.advertencia > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">{conflicts.counts.advertencia} avisos</span>}
                {conflicts.counts?.info > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">{conflicts.counts.info} info</span>}
              </div>
              <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {conflicts.conflicts.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 px-3 py-2 text-sm" data-testid={`conflict-${c.severity}-${i}`}>
                    <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${c.severity === "error" ? "bg-red-500" : c.severity === "advertencia" ? "bg-amber-500" : "bg-sky-500"}`} />
                    <span className="text-slate-700">{c.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {view === "weekly" && (
            <div className="overflow-x-auto rounded-xl border border-purple-200 bg-white" data-testid="weekly-grid">
              <table className="w-full border-collapse min-w-[720px]">
                <thead>
                  <tr>
                    <th className="bg-purple-700 text-white text-xs font-black uppercase tracking-wide px-2 py-3 w-20 sticky left-0 z-10">Hora</th>
                    {activeDays.map((d) => (
                      <th key={d.key} className="bg-purple-700 text-white text-xs font-black uppercase tracking-wide px-2 py-3">{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slots.length === 0 && (
                    <tr><td colSpan={activeDays.length + 1} className="text-center text-slate-400 py-10" data-testid="weekly-no-slots">
                      Sin franjas todavía. {isAdmin && "Pulsa “Añadir franja”."}
                    </td></tr>
                  )}
                  {slots.map(([st, et]) => (
                    <tr key={`${st}-${et}`} className="border-t border-purple-100">
                      <td className="bg-purple-700 text-white text-xs font-bold text-center px-1 py-2 sticky left-0">{st}<br />{et}</td>
                      {activeDays.map((d) => {
                        const cs = cellEntries(d.key, st, et);
                        return (
                          <td key={d.key} className="align-top px-1.5 py-1.5 border-l border-purple-50 min-w-[130px]"
                            onDragOver={isAdmin ? (e) => e.preventDefault() : undefined}
                            onDrop={isAdmin ? (e) => onDropCell(e, d.key, st, et) : undefined}
                            data-testid={`cell-${d.key}-${st}`}>
                            <div className="space-y-1">
                              {cs.map((e) => (
                                <EntryChip key={e.id} entry={e} isAdmin={isAdmin}
                                  onEdit={() => { setEntryDlg({ mode: "edit", data: { ...e } }); }}
                                  onDelete={() => deleteEntry(e.id)} />
                              ))}
                              {isAdmin && (
                                <button
                                  className="w-full text-[11px] text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded py-0.5 font-semibold"
                                  onClick={() => setEntryDlg({ mode: "create", data: { ...emptyEntry(), day_of_week: d.key, start_time: st, end_time: et } })}
                                  data-testid={`cell-add-${d.key}-${st}`}>+ añadir</button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isAdmin && (
                <div className="p-3 border-t border-purple-100">
                  <Button variant="outline" size="sm"
                    onClick={() => setEntryDlg({ mode: "create", data: emptyEntry() })}
                    data-testid="weekly-add-slot">
                    <Plus className="h-4 w-4 mr-1.5" /> Añadir franja
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* BY TEAM */}
          {view === "team" && (
            <div className="bg-white border border-slate-200 rounded-xl divide-y" data-testid="team-view">
              {(() => {
                const byTeam = {};
                filtered.forEach((e) => { (byTeam[e.team_id] ||= []).push(e); });
                const ids = Object.keys(byTeam);
                if (!ids.length) return <div className="py-10 text-center text-slate-400">Sin franjas para los filtros aplicados.</div>;
                return ids.map((tid) => (
                  <div key={tid} className="p-4" data-testid={`team-row-${tid}`}>
                    <div className="font-bold text-slate-900 mb-2">{teamName(tid)}</div>
                    <div className="flex flex-wrap gap-2">
                      {byTeam[tid].sort((a, b) => DAYS.findIndex((d) => d.key === a.day_of_week) - DAYS.findIndex((d) => d.key === b.day_of_week) || a.start_time.localeCompare(b.start_time)).map((e) => (
                        <div key={e.id} className="text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                          <span className="font-bold text-purple-800">{DAYS.find((d) => d.key === e.day_of_week)?.label}</span>{" "}
                          {e.start_time}–{e.end_time}
                          <span className="text-slate-500"> · {e.facility_name}{e.zone_name ? ` (${e.zone_name})` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* BY FACILITY */}
          {view === "facility" && (
            <div className="bg-white border border-slate-200 rounded-xl divide-y" data-testid="facility-view">
              {(() => {
                const byFac = {};
                filtered.forEach((e) => { (byFac[e.facility_name || "Sin instalación"] ||= []).push(e); });
                const names = Object.keys(byFac);
                if (!names.length) return <div className="py-10 text-center text-slate-400">Sin franjas para los filtros aplicados.</div>;
                return names.map((fn) => (
                  <div key={fn} className="p-4">
                    <div className="font-bold text-purple-800 mb-2 flex items-center gap-2"><Building2 className="h-4 w-4" />{fn}</div>
                    <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                      {byFac[fn].sort((a, b) => DAYS.findIndex((d) => d.key === a.day_of_week) - DAYS.findIndex((d) => d.key === b.day_of_week) || a.start_time.localeCompare(b.start_time)).map((e) => (
                        <div key={e.id} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5">
                          <span className="font-bold">{DAYS.find((d) => d.key === e.day_of_week)?.label}</span> {e.start_time}–{e.end_time}
                          <div className="text-slate-600">{e.team_name}{e.zone_name ? ` · ${e.zone_name}` : ""}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </>
      )}

      {/* ---------- Schedule dialog ---------- */}
      <Dialog open={!!schedDlg} onOpenChange={(o) => !o && setSchedDlg(null)}>
        <DialogContent data-testid="sched-dialog">
          <DialogHeader>
            <DialogTitle>
              {schedDlg?.mode === "create" ? "Nuevo horario" : schedDlg?.mode === "duplicate" ? "Duplicar horario para nueva temporada" : "Editar horario"}
            </DialogTitle>
            {schedDlg?.mode === "duplicate" && (
              <DialogDescription>Se copian días, horas, equipos e instalaciones. No se copian entrenamientos, asistencias ni historial.</DialogDescription>
            )}
          </DialogHeader>
          {schedDlg && (
            <div className="space-y-3">
              <div>
                <Label>Temporada</Label>
                <Input value={schedDlg.data.season_label} onChange={(e) => setSchedDlg({ ...schedDlg, data: { ...schedDlg.data, season_label: e.target.value } })} placeholder="2026-2027" data-testid="sched-f-season" />
              </div>
              <div>
                <Label>Nombre</Label>
                <Input value={schedDlg.data.name} onChange={(e) => setSchedDlg({ ...schedDlg, data: { ...schedDlg.data, name: e.target.value } })} placeholder="Horario general" data-testid="sched-f-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inicio</Label>
                  <Input type="date" value={schedDlg.data.start_date} onChange={(e) => setSchedDlg({ ...schedDlg, data: { ...schedDlg.data, start_date: e.target.value } })} data-testid="sched-f-start" /></div>
                <div><Label>Fin</Label>
                  <Input type="date" value={schedDlg.data.end_date} onChange={(e) => setSchedDlg({ ...schedDlg, data: { ...schedDlg.data, end_date: e.target.value } })} data-testid="sched-f-end" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedDlg(null)}>Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={saveSchedule} disabled={busy} data-testid="sched-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Period dialog ---------- */}
      <Dialog open={!!periodDlg} onOpenChange={(o) => !o && setPeriodDlg(null)}>
        <DialogContent data-testid="period-dialog">
          <DialogHeader>
            <DialogTitle>{periodDlg?.mode === "create" ? "Nuevo periodo especial" : "Editar periodo especial"}</DialogTitle>
            <DialogDescription>Sustituye temporalmente el horario general para los equipos seleccionados. Al terminar (o al borrarlo) vuelve el general automáticamente.</DialogDescription>
          </DialogHeader>
          {periodDlg && (
            <div className="space-y-3">
              <div><Label>Nombre</Label>
                <Input value={periodDlg.data.name} onChange={(e) => setPeriodDlg({ ...periodDlg, data: { ...periodDlg.data, name: e.target.value } })} placeholder="Horario de Navidad" data-testid="period-f-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Desde</Label>
                  <Input type="date" value={periodDlg.data.start_date} onChange={(e) => setPeriodDlg({ ...periodDlg, data: { ...periodDlg.data, start_date: e.target.value } })} data-testid="period-f-start" /></div>
                <div><Label>Hasta</Label>
                  <Input type="date" value={periodDlg.data.end_date} onChange={(e) => setPeriodDlg({ ...periodDlg, data: { ...periodDlg.data, end_date: e.target.value } })} data-testid="period-f-end" /></div>
              </div>
              <div><Label>Motivo (opcional)</Label>
                <Input value={periodDlg.data.reason || ""} onChange={(e) => setPeriodDlg({ ...periodDlg, data: { ...periodDlg.data, reason: e.target.value } })} placeholder="Navidad, pretemporada, obras…" data-testid="period-f-reason" /></div>
              <div>
                <Label>Equipos afectados</Label>
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1" data-testid="period-teams">
                  {teams.map((t) => {
                    const on = (periodDlg.data.team_ids || []).includes(t.id);
                    return (
                      <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                        <input type="checkbox" checked={on} data-testid={`period-team-${t.id}`}
                          onChange={(e) => {
                            const set = new Set(periodDlg.data.team_ids || []);
                            if (e.target.checked) set.add(t.id); else set.delete(t.id);
                            setPeriodDlg({ ...periodDlg, data: { ...periodDlg.data, team_ids: Array.from(set) } });
                          }} />
                        {t.name}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">Solo se suprime el horario general de estos equipos durante el periodo.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriodDlg(null)}>Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={savePeriod} disabled={busy} data-testid="period-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Entry dialog ---------- */}
      <Dialog open={!!entryDlg} onOpenChange={(o) => !o && setEntryDlg(null)}>
        <DialogContent data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle>{entryDlg?.mode === "create" ? "Añadir asignación" : "Editar asignación"}</DialogTitle>
          </DialogHeader>
          {entryDlg && (
            <div className="space-y-3">
              <div>
                <Label>Equipo</Label>
                <Select value={entryDlg.data.team_id} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, team_id: v } })}>
                  <SelectTrigger data-testid="entry-team"><SelectValue placeholder="Elige equipo" /></SelectTrigger>
                  <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Día</Label>
                  <Select value={entryDlg.data.day_of_week} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, day_of_week: v } })}>
                    <SelectTrigger data-testid="entry-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Intervalo</Label>
                  <Select value={String(step)} onValueChange={(v) => setStep(Number(v))}>
                    <SelectTrigger data-testid="entry-step"><SelectValue /></SelectTrigger>
                    <SelectContent>{STEP_OPTS.map((o) => <SelectItem key={o} value={String(o)}>{o} min</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inicio</Label>
                  <Input type="time" step={step * 60} value={entryDlg.data.start_time} onChange={(e) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, start_time: e.target.value } })} data-testid="entry-start" /></div>
                <div><Label>Fin</Label>
                  <Input type="time" step={step * 60} value={entryDlg.data.end_time} onChange={(e) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, end_time: e.target.value } })} data-testid="entry-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Instalación</Label>
                  <Select value={entryDlg.data.facility_id || "none"} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, facility_id: v === "none" ? "" : v, field_area_id: "" } })}>
                    <SelectTrigger data-testid="entry-facility"><SelectValue placeholder="Instalación" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin instalación —</SelectItem>
                      {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Campo / zona</Label>
                  <Select value={entryDlg.data.field_area_id || "none"} onValueChange={(v) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, field_area_id: v === "none" ? "" : v } })} disabled={!entryDlg.data.facility_id}>
                    <SelectTrigger data-testid="entry-zone"><SelectValue placeholder="Zona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin zona —</SelectItem>
                      {zoneOptions(entryDlg.data.facility_id).map((z) => (
                        <SelectItem key={z.id} value={z.id} data-testid={`entry-zone-opt-${z.id}`}>
                          {z.name}{z.std ? "  ·  estándar" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-1">Asigna «Medio campo 1» a un equipo y «Medio campo 2» a otro para que entrenen a la vez sin conflicto.</p>
                </div>
              </div>
              <div><Label>Observaciones</Label>
                <Textarea rows={2} value={entryDlg.data.notes || ""} onChange={(e) => setEntryDlg({ ...entryDlg, data: { ...entryDlg.data, notes: e.target.value } })} data-testid="entry-notes" /></div>
              {entryDlg.mode === "edit" && (
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500">Copiar a:</span>
                  {DAYS.slice(0, 5).map((d) => (
                    <button key={d.key} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-purple-50 hover:border-purple-300"
                      onClick={() => copyToDay(entryDlg.data, d.key)} data-testid={`entry-copy-${d.key}`}>{d.label.slice(0, 3)}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDlg(null)}>Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={saveEntry} disabled={busy} data-testid="entry-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Facilities dialog ---------- */}
      <FacilitiesDialog open={facDlg} onClose={() => setFacDlg(false)} facilities={facilities}
        reload={async () => { const r = await api.get("/schedules/facilities"); setFacilities(r.data); }} />

      {/* ---------- Publish preview ---------- */}
      <Dialog open={!!publishDlg} onOpenChange={(o) => !o && setPublishDlg(null)}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle>Vista previa de sincronización</DialogTitle>
            <DialogDescription>Se aplicará solo a entrenamientos futuros de origen automático. Los pasados y los modificados manualmente quedan intactos.</DialogDescription>
          </DialogHeader>
          {publishDlg && (
            <div className="grid grid-cols-2 gap-3">
              <PreviewStat label="Nuevos" value={publishDlg.created} cls="text-emerald-700" icon={CheckCircle2} />
              <PreviewStat label="Se actualizan" value={publishDlg.updated} cls="text-sky-700" icon={CalendarDays} />
              <PreviewStat label="Se cancelan" value={publishDlg.cancelled} cls="text-red-700" icon={X} />
              <PreviewStat label="Protegidos (manual)" value={publishDlg.protected} cls="text-amber-700" icon={AlertTriangle} />
            </div>
          )}
          {conflicts.counts?.error > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="publish-conflicts-warning">
              <b>{conflicts.counts.error} conflicto(s) grave(s)</b> detectado(s). Revisa la lista de conflictos. Puedes publicar de todas formas bajo tu responsabilidad.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDlg(null)}>Cancelar</Button>
            {conflicts.counts?.error > 0 ? (
              <Button className="bg-red-600 hover:bg-red-700 text-white font-bold" onClick={() => confirmPublish(true)} disabled={busy} data-testid="publish-force-btn">
                Publicar de todas formas
              </Button>
            ) : (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={() => confirmPublish(false)} disabled={busy} data-testid="publish-confirm-btn">
                Confirmar y sincronizar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[150px] h-9 text-sm" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function EntryChip({ entry, isAdmin, onEdit, onDelete }) {
  return (
    <div
      draggable={isAdmin}
      onDragStart={isAdmin ? (e) => e.dataTransfer.setData("text/entry", entry.id) : undefined}
      className={`group relative rounded-md border px-2 py-1 text-[11px] leading-tight bg-white ${isAdmin ? "cursor-grab hover:border-purple-400" : ""} border-slate-200`}
      data-testid={`entry-chip-${entry.id}`}>
      <div className="font-bold text-purple-800 pr-8 break-words">{entry.team_name}</div>
      {(entry.facility_name || entry.zone_name) && (
        <div className="text-slate-500">{[entry.facility_name, entry.zone_name].filter(Boolean).join(" · ")}</div>
      )}
      {isAdmin && (
        <div className="absolute top-0.5 right-0.5 hidden group-hover:flex gap-0.5">
          <button onClick={onEdit} className="text-slate-400 hover:text-purple-700" data-testid={`entry-edit-${entry.id}`}><Pencil className="h-3 w-3" /></button>
          <button onClick={onDelete} className="text-slate-400 hover:text-red-600" data-testid={`entry-del-${entry.id}`}><X className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );
}

function PreviewStat({ label, value, cls, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 flex items-center gap-3">
      <Icon className={`h-5 w-5 ${cls}`} />
      <div>
        <div className={`text-2xl font-black ${cls}`}>{value}</div>
        <div className="text-xs text-slate-500 font-semibold">{label}</div>
      </div>
    </div>
  );
}

function FacilitiesDialog({ open, onClose, facilities, reload }) {
  const [editing, setEditing] = useState(null); // {id?, name, zones:[{id?,name}]}
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Nombre requerido"); return; }
    setBusy(true);
    try {
      const body = { name: editing.name.trim(), zones: (editing.zones || []).filter((z) => z.name.trim()), active: true };
      if (editing.id) await api.put(`/schedules/facilities/${editing.id}`, body);
      else await api.post("/schedules/facilities", body);
      await reload(); setEditing(null); toast.success("Instalación guardada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminar esta instalación?")) return;
    try { await api.delete(`/schedules/facilities/${id}`); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setEditing(null); } }}>
      <DialogContent className="max-w-lg" data-testid="facilities-dialog">
        <DialogHeader><DialogTitle>Instalaciones y zonas</DialogTitle></DialogHeader>
        {!editing ? (
          <div className="space-y-2">
            {facilities.map((f) => (
              <div key={f.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`fac-row-${f.id}`}>
                <div>
                  <div className="font-bold text-slate-900">{f.name}</div>
                  <div className="text-xs text-slate-500">{f.zones.map((z) => z.name).join(", ") || "Sin zonas"}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...f, zones: f.zones.map((z) => ({ ...z })) })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(f.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button variant="outline" className="w-full" onClick={() => setEditing({ name: "", zones: [] })} data-testid="fac-new-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nueva instalación
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label>Nombre</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="fac-name" /></div>
            <div>
              <Label>Zonas</Label>
              <div className="space-y-1.5">
                {(editing.zones || []).map((z, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={z.name} onChange={(e) => { const zs = [...editing.zones]; zs[i] = { ...zs[i], name: e.target.value }; setEditing({ ...editing, zones: zs }); }} />
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setEditing({ ...editing, zones: editing.zones.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, zones: [...(editing.zones || []), { name: "" }] })} data-testid="fac-add-zone">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Añadir zona
                  </Button>
                  <Button size="sm" variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="fac-add-standard-zones"
                    onClick={() => {
                      const existing = new Set((editing.zones || []).map((z) => (z.name || "").trim().toLowerCase()));
                      const toAdd = STANDARD_ZONES.filter((n) => !existing.has(n.toLowerCase())).map((n) => ({ name: n }));
                      setEditing({ ...editing, zones: [...(editing.zones || []), ...toAdd] });
                    }}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Zonas estándar
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  "Zonas estándar" añade Campo completo, Medio campo 1/2, Cuarto 1–4 y Fondo 1/2.
                  Los conflictos entienden qué subdivisiones se solapan físicamente.
                </p>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(null)}>Volver</Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={save} disabled={busy} data-testid="fac-save-btn">Guardar</Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
